import React from 'react';
import { LayersIcon } from 'lucide-react';
import { Card, CardHeader } from './Card';
import type { LoanOfficer, PipelineStage } from '../types/commission';
import { calcOverageDollars, summarizePipeline } from '../utils/commission';
import { formatCompactCurrency, formatCurrency, formatPercent, formatPrice } from '../utils/format';
const stageStyles: Record<PipelineStage, string> = {
  'Clear to Close': 'bg-emerald-100 text-emerald-800',
  'Docs Out': 'bg-brand-100 text-brand-800',
  Approved: 'bg-sky-100 text-sky-800',
  Processing: 'bg-slate-100 text-slate-700'
};
interface PipelinePanelProps {
  officer: LoanOfficer;
}
export function PipelinePanel({
  officer
}: PipelinePanelProps) {
  const summary = summarizePipeline(officer.pipeline);
  return <Card data-id="element-150" data-mp="3mw4ckb7ba5db">
      <CardHeader title="Active pipeline" description="Weighted by pull-through to drive the Current Pacing column." icon={<LayersIcon className="h-4 w-4" aria-hidden="true" data-id="element-152" data-mp="jggubvdr9enb" />} action={<div className="text-right" data-id="element-153" data-mp="miqibx5a1evj">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-154" data-mp="mjs3wvjx5jri">
              Weighted
            </p>
            <p className="text-sm font-semibold tabular-nums text-slate-900" data-id="element-155" data-mp="rk611ijzf9ar">
              {summary.weightedUnits.toFixed(1)} loans ·{' '}
              {formatCompactCurrency(summary.weightedVolume)}
            </p>
          </div>} data-id="element-151" data-mp="o88adbzac1z7" />
      {officer.pipeline.length === 0 ? <p className="px-5 py-10 text-center text-sm text-slate-500" data-id="element-156" data-mp="3utwwx3ihrfb">
          No active loans in the pipeline — the pacing column matches actuals.
        </p> : <ul className="divide-y divide-slate-100" data-id="element-157" data-mp="3qz1d5dzyqb8p">
          {officer.pipeline.map(loan => <li key={loan.id} className="flex items-center justify-between gap-4 px-5 py-3" data-id="element-158" data-mp="3uzk7pft8pvel">
              <div className="min-w-0" data-id="element-159" data-mp="1p7esdu6nzcko">
                <p className="truncate text-sm font-semibold text-slate-900" data-id="element-160" data-mp="1p496entc0qt6">{loan.borrower}</p>
                <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500" data-id="element-161" data-mp="1t5cicz89e2p4">
                  <span className={`inline-flex rounded-full px-2 py-0.5 font-semibold ${stageStyles[loan.stage]}`} data-id="element-162" data-mp="3229c3qabci5i">
                    {loan.stage}
                  </span>
                  <span data-id="element-163" data-mp="2o65x3480ggdm">Est. {loan.expectedCloseDate}</span>
                  <span data-id="element-164" data-mp="3kuvg4xvdjf16">Price {formatPrice(loan.sellPrice)}</span>
                </p>
              </div>
              <div className="shrink-0 text-right" data-id="element-165" data-mp="3gts1ufsz5r22">
                <p className="text-sm font-semibold tabular-nums text-slate-900" data-id="element-166" data-mp="3gubilt6w29ei">
                  {formatCurrency(loan.loanAmount)}
                </p>
                <p className="mt-0.5 text-xs tabular-nums text-slate-500" data-id="element-167" data-mp="3748h14lpbn4k">
                  {formatPercent(loan.likelihood)} pull-through ·{' '}
                  {formatCurrency(calcOverageDollars(loan.loanAmount, loan.sellPrice))} overage
                </p>
              </div>
            </li>)}
        </ul>}
      <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50 px-5 py-3 text-xs text-slate-600" data-id="element-168" data-mp="azg02sf4ee26">
        <span data-id="element-169" data-mp="fzbwo6lhn37p">
          {summary.grossUnits} active loans · {formatCurrency(summary.grossVolume)} gross
        </span>
        <span className="font-semibold tabular-nums text-slate-800" data-id="element-170" data-mp="oxxluklgtghg">
          {formatCurrency(summary.weightedOverageDollars)} weighted overage
        </span>
      </div>
    </Card>;
}